import type {
  RemoveDlnItemLinkMutationOptions,
  RemoveDlnItemLinkVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysInvoice } from '../queryKeys';

import { DLN_ITEM_LINK_MUTATION_KEY } from './constants';
import { removeDlnItemLink } from './removeDlnItemLink';
import { writeInvoiceDetail } from './writeInvoiceDetail';

/**
 * Removes one manual delivery note link from a line item. The returned
 * invoice replaces the cached detail, and every linkable-line query is
 * refetched.
 *
 * @param options - Optional mutation configuration; callbacks run after the hook's own.
 * @returns The mutation; `isPending` lasts until the caches are settled.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/unlink_dln_item_invoices__invoice_id__line_items__line_item_id__dln_item_links__link_id__delete
 */
export function useMutationRemoveDlnItemLink(
  options?: RemoveDlnItemLinkMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (variables: RemoveDlnItemLinkVariables) =>
      removeDlnItemLink(variables),
    mutationKey: DLN_ITEM_LINK_MUTATION_KEY,
    onSettled(data, error, variables, onMutateResult, context) {
      return Promise.all([
        writeInvoiceDetail(queryClient, variables.invoiceId, data),
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getLinkableDlnItemsAll(),
        }),
        options?.onSettled?.(data, error, variables, onMutateResult, context),
      ]);
    },
  });
}
